import { computed, signal } from '@angular/core';
import {
  EFindingCardSortMode,
  IFindingCardData,
  IFindingCardJump,
  IFindingCardsObjective,
  IFindingCardsStateOptions,
} from './finding-card.interface';
import { getNextFindingCardId, numberFindingCardsChronologically, sortFindingCards } from './finding-card.util';

// Card grid state shared by every deliverable detail view that shows findings as cards. Composed into the
// component (rather than a base class) because stream and post views extend different base directives.
export class FindingCardsState<T extends IFindingCardData> {
  public readonly cards = signal<T[]>([]);
  public readonly sortMode = signal<EFindingCardSortMode>(EFindingCardSortMode.time);
  // Set only by user actions; the post base's automatic first-row selection must not highlight a card.
  public readonly highlightedId = signal<string | null>(null);
  public readonly sortedCards = computed(() => sortFindingCards(this.cards(), this.sortMode()));
  public readonly activeSortedCards = computed(() => {
    const removedIds = this.options.removedIds();

    return this.sortedCards().filter((card) => !removedIds.has(card.id));
  });
  public readonly pendingDeletionIds = computed(
    () =>
      new Set(
        this.cards()
          .filter((card) => this.options.isPendingDeletion(card))
          .map((card) => card.id),
      ),
  );
  public readonly loading = computed(() => this.options.loading());
  // Export mode reads images through the export route; briefing mode has none (and never shows findings).
  public readonly imageCampaignExportId = computed(() => {
    const { exportMode, campaignExportId } = this.options.manualDeliverableStatusConfig();

    return exportMode ? campaignExportId : null;
  });
  public readonly canLoadImages = computed(() => {
    const { exportMode, creatorBriefingMode } = this.options.manualDeliverableStatusConfig();

    return !creatorBriefingMode && (!exportMode || !!this.imageCampaignExportId());
  });
  public readonly objective = computed<IFindingCardsObjective | null>(() => {
    if (!this.options.manualDeliverableStatusConfig().analyticsVisible) {
      return null;
    }

    return {
      findingsCount: this.activeSortedCards().length,
      jump: (): void => this.jumpToNextCard(),
      loading: this.loading(),
      status: this.options.status(),
    };
  });

  private lastJump: IFindingCardJump | null = null;

  constructor(private readonly options: IFindingCardsStateOptions<T>) {}

  // Numbers the rows chronologically, stores them and returns them for the component's table data source.
  public setCards(rows: T[]): T[] {
    const cards = numberFindingCardsChronologically(rows);

    this.cards.set(cards);

    return cards;
  }

  // Own cursor instead of the selection: the first jump starts at the top of the current order on every
  // platform, and a sort change restarts it there.
  private jumpToNextCard(): void {
    const sortMode = this.sortMode();
    const previousCardId = this.lastJump?.sortMode === sortMode ? this.lastJump.cardId : null;
    const nextCardId = getNextFindingCardId(this.activeSortedCards(), previousCardId);

    if (nextCardId) {
      this.lastJump = { cardId: nextCardId, sortMode };
      this.options.onJump(nextCardId);
    }
  }
}
